import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import Layout from "./components/layout/Layout";
import LoginPage from "./components/auth/LoginPage";

// Pages
import Dashboard from "./pages/dashboard/Dashboard";
import Users from "./pages/users/Users";
import Wallets from "./pages/wallets/Wallets";
import CreditPacks from "./pages/credits/CreditPacks";
import CreditOrders from "./pages/credits/CreditOrders";
import Zodiacs from "./pages/zodiacs/Zodiacs";
import Horoscope from "./pages/horoscope/Horoscope";
import Kundli from "./pages/kundli/Kundli";
import Reports from "./pages/reports/Reports";
import AuditLogs from "./pages/audit/AuditLogs";
import AdminUsers from "./pages/admins/AdminUsers";
import ServiceManagement from "./pages/services/ServiceManagementPage";
import Security from "./pages/security/Security";
import UserWalletPage from "./pages/users/UserWalletPage";
import Banners from "./pages/banners/Banners";
import LegalContent from "./pages/legal/LegalContent";
import SupportQueries from "./pages/support/SupportQueries";
import ContactSupportInfo from "./pages/contact/ContactSupportInfo";
import SocialLinks from "./pages/contact/SocialLinks";
import AppVersions from "./pages/appversions/AppVersions";
import MyProfile from "./pages/profile/MyProfile";
import ChangePassword from "./pages/profile/ChangePassword";
import DeletedUsers from "./pages/users/DeletedUsers";

const ComingSoon = () => <Navigate to="/dashboard" replace />;

function PrivateRoute({ children }) {
  const { user, initializing } = useAuth();

  if (initializing) return null;
  if (!user) return <Navigate to="/login" replace />;

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, initializing } = useAuth();

  if (initializing) return null;
  if (user) return <Navigate to="/" replace />;

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

      <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />

      <Route path="/profile"         element={<PrivateRoute><MyProfile /></PrivateRoute>} />
      <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />

      <Route path="/users"                    element={<PrivateRoute><Users /></PrivateRoute>} />
      <Route path="/users/wallet/:userId"     element={<PrivateRoute><UserWalletPage /></PrivateRoute>} />
      <Route path="/users/deleted"            element={<PrivateRoute><DeletedUsers /></PrivateRoute>} />

      <Route path="/wallets"       element={<PrivateRoute><Wallets /></PrivateRoute>} />
      <Route path="/credit-packs"  element={<PrivateRoute><CreditPacks /></PrivateRoute>} />
      <Route path="/credit-orders" element={<PrivateRoute><CreditOrders /></PrivateRoute>} />

      <Route path="/zodiacs"  element={<PrivateRoute><Zodiacs /></PrivateRoute>} />
      <Route path="/services" element={<PrivateRoute><ServiceManagement /></PrivateRoute>} />
      <Route path="/horoscope" element={<PrivateRoute><Horoscope /></PrivateRoute>} />
      <Route path="/kundli"   element={<PrivateRoute><Kundli /></PrivateRoute>} />

      <Route path="/banners" element={<PrivateRoute><Banners /></PrivateRoute>} />

      <Route path="/audit"   element={<PrivateRoute><AuditLogs /></PrivateRoute>} />
      <Route path="/admins"  element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />

      {/* Legal */}
      <Route path="/legal" element={<Navigate to="/legal/terms-and-conditions" replace />} />
      <Route path="/legal/terms-and-conditions" element={<PrivateRoute><LegalContent /></PrivateRoute>} />
      <Route path="/legal/privacy-policy"       element={<PrivateRoute><LegalContent /></PrivateRoute>} />
      <Route path="/legal/faq"                  element={<PrivateRoute><LegalContent /></PrivateRoute>} />

      <Route path="/menu"     element={<ComingSoon />} />
      <Route path="/security" element={<ComingSoon />} />

      <Route path="/support-queries" element={<PrivateRoute><SupportQueries /></PrivateRoute>} />

      {/* App Support — parent redirects to first child */}
      <Route path="/app-support" element={<Navigate to="/app-support/contact-support-info" replace />} />
      <Route path="/app-support/contact-support-info" element={<PrivateRoute><ContactSupportInfo /></PrivateRoute>} />
      <Route path="/app-support/social-links"         element={<PrivateRoute><SocialLinks /></PrivateRoute>} />

      {/* Legacy flat path — redirect to nested */}
      <Route path="/contact-support-info" element={<Navigate to="/app-support/contact-support-info" replace />} />

      <Route path="/app-versions" element={<PrivateRoute><AppVersions /></PrivateRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SidebarProvider>
          <AppRoutes />
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#0F3D3E",
                color: "#e0f4f4",
                fontSize: "13px",
                fontWeight: 500,
                fontFamily: "var(--font-body)",
                borderRadius: "10px",
                padding: "12px 18px",
                boxShadow: "0 4px 20px rgba(15,61,62,0.45)",
              },
              success: {
                iconTheme: { primary: "#4ade80", secondary: "#0F3D3E" },
              },
              error: {
                duration: 4000,
                style: {
                  background: "#3d0f0f",
                  color: "#fecaca",
                  boxShadow: "0 4px 20px rgba(61,15,15,0.45)",
                },
                iconTheme: { primary: "#f87171", secondary: "#3d0f0f" },
              },
            }}
          />
        </SidebarProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}